import { cookieService } from '@/services/cookie.service';
import { useAuthActions } from '@/store/auth';
import { useSiteStore } from '@/store/site';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { CFS, MD5 } from '@/utils/cfsEncodeFunc';
import { createUserSession } from '@nf/services/actions/auth/session';
import { SwitchFromEnum } from '@nf/types/site';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { convertApiLocale, convertClientLocale } from '@nf/utils-common/site';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname, useRouter, redirect } from '@/navigation';
import { ChangeEvent, startTransition, useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { switchViewService } from '@/services/swtich-view.service';
import { rumLogger } from '@/features/providers/openobserve-provider';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { encryptRawPassword, getLoginPublicKey } from '@/utils/password-migration';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { useApiBackendInfo } from '@/hooks/query';

type LoginData = {
	username: string,
	password: string,
	validation: string,
	language: string,
	rememberMe: boolean
}

type SuccessLoginData = {
	Language: string,
	SiteName: string,
	Skin: number,
	Token: string,
	Url: string,
	UserName: string,
}

const initialLoginData = {
	username: '',
	password: '',
	validation: '',
	language: '',
	rememberMe: false,
}

const initialErrorMessage = {
	username: '',
	password: '',
	validation: '',
	resError: '',
}

export const useMemberLogin = () => {
	const t = useTranslations('General');
	const router = useRouter();
	const apiDomain = useSiteStore((state) => state.apiDomain);
	const [codeImgSrc, setCodeImgSrc] = useState('');
	const uuid = uuidv4();
	const { signIn } = useAuthActions();
	const currentLocale = useLocale();
	const [validationToken, setValidationToken] = useState('');
	const [isLogining, setIsLogining] = useState(false);
	const [loginData, setLoginData] = useState<LoginData>(initialLoginData);
	const [errorMessage, setErrorMessage] = useState(initialErrorMessage);
	const pathName = usePathname();
	const { url } = useApiBackendInfo();
	
	function initLoginData() {
		const lastestUsername = cookieService.getUserNames()[0] || '';
		const isRememberMe = cookieService.getRememberMe();
		if (isRememberMe) {
			setLoginData({ ...initialLoginData, username: lastestUsername, rememberMe: isRememberMe });
		} else {
			setLoginData(initialLoginData);
		}
		initErrorMessage();
	}

	function initErrorMessage() {
		setErrorMessage(initialErrorMessage);
	}
	
	async function getCodeImgSrc(domain: string) {
		try {
			const response = await fetchComposer.post(cloudUrlParser(`${domain}/api/athenalogin/CreateRandomCheckCode`), {});
			if (response.ok) {
				const newToken = response.headers.get('x-jwtoken');
				newToken && setValidationToken(newToken);
				const blob = await response.blob();
				const base64String = await blobToBase64(blob);
				base64String && setCodeImgSrc(base64String);
			} 
		} catch (e) {
			throw e;
		}
	}
	const refetchCodeImgSrc = useCallback(async () => {
		apiDomain.ctcdDomain && await getCodeImgSrc(apiDomain.ctcdDomain);
	}, [apiDomain.ctcdDomain]);

	function checkPasswordLoginData (loginData: LoginData) {
		let newErrorMessage = {};
		let isError = false;
		initErrorMessage();
		if (!loginData.username) {
			newErrorMessage = { ...newErrorMessage, username: 'lbl_EmptyField' };
			isError = true;
		}
	
		if (!loginData.password) {
			newErrorMessage = { ...newErrorMessage, password: 'lbl_EmptyField' };
			isError = true;
		}
	
		if (!loginData.validation) {
			newErrorMessage = { ...newErrorMessage, validation: 'lbl_EmptyField' };
			isError = true;
		}
		setErrorMessage(state => ({ ...state, ...newErrorMessage }));
		return isError;
	}
	
	

	function onChange(event: ChangeEvent<HTMLInputElement>) {
		const id = event.target.id as keyof typeof errorMessage;
		const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
		setLoginData(state => ({
			...state,
			[id]: value
		}));
		if (errorMessage[id]) {
			initErrorMessage();
		}
	}

	function handleRememberMe() {
		if (loginData.rememberMe) {
			cookieService.addUserNames(loginData.username);
			cookieService.setRememberMe(true);
		} else {
			cookieService.removeAllUserName();
			cookieService.setRememberMe(false);
		}
	}

	function handleGestureLogin() {
		cookieService.addUserNames(loginData.username);
		cookieService.addGestureUser(loginData.username);
	}

	async function onLogin(
		loginData: LoginData,
		{
			forceToLocale,
			switchFrom,
			enableRedirect
		}: {
			forceToLocale?: boolean,
			switchFrom?: SwitchFromEnum,
			enableRedirect?: boolean
		}
	) {
		try {
			if (isLogining) return;
			setIsLogining(true);
			initErrorMessage();

			const legacyPassword = MD5(CFS(loginData.password) + loginData.validation);
			let rsaPassword: string | null = null;

			const publicKey = await getLoginPublicKey(url);
			rsaPassword = encryptRawPassword(loginData.password, publicKey);

			const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiDomain.ctcdDomain}/api/AthenaLogin/Login`),  {
				body: JSON.stringify({
					TxtUserName: loginData.username,
					Password: legacyPassword,
					RsaPassword: rsaPassword,
					IsGesture: false,
					PasswordEncodeKey: loginData.validation,
					SwitchFrom: switchFrom,
					Language: convertApiLocale(loginData.language || currentLocale),
				}) })(validationToken);
			
			const data: { Code: string, Message: string, Success: boolean, Data: any } = await response.json();
		
			if (data.Success) {
				cookieService.addUserNames(loginData.username);
				const result = data.Data as SuccessLoginData;	
				// 若有token 則在原站登入跳轉畫面
				if (result.Token) {
					const redisResult = await createUserSession({
						name: result.UserName,
						site: result.SiteName,
						uuid,
					}, {
						token: result.Token
					});
					
					if (!redisResult.success) {
						throw { resError: 'Error: Insert to redis fail' };
					}

					if (result.Skin) {
						switchViewService.switchView({
							view: result.Skin,
							href: ''
						});
					}
					
					signIn('', result.UserName, result.SiteName, uuid);
					rumLogger.info('Login success', { username: result.UserName, siteName: result.SiteName, loginType: 'password' });
					handleRememberMe();
					const clientLocaleFromApi = convertClientLocale(result.Language);
					// 如果需要切換語系則保持isLogining
					if ((clientLocaleFromApi !== currentLocale) || forceToLocale) {
						await startTransition(() => {
							router.replace(pathName, { locale: clientLocaleFromApi });
						})
					} else {
						setIsLogining(false);
					}
				} else if (enableRedirect && result.Url) {
					window.location.replace(result.Url)
				}
				return result;
			}

			throw { code: data.Code, resError: `${data.Code}: ${data.Message}` };
		} catch (errors: any) {
			switch (errors.code) {
				case 'SP-1011':
					// wrong account or password
					setErrorMessage(state => ({ ...state, resError: errors.code + ': ' + t('msg_DepositProcessLogin_15') }));
					break;
				case 'SP-1006':
					// wrong validation
					setErrorMessage(state => ({ ...state, resError: errors.code + ': ' + t('lbl_ValidationCodeError') }));
					break;
				case 'SP-1008':
					// locked
					setErrorMessage(state => ({ ...state, resError: errors.code + ': ' + t('msg_DepositProcessLogin_6') }));
					break;
				case 'SP-1009':
					// login too many times
					setErrorMessage(state => ({ ...state, resError: errors.code + ': ' + t('errlogin_toooften') }));
					break;
				default:
					setErrorMessage(state => ({ ...state, resError: 'Login Error', ...errors }));
					break;
			}
			rumLogger.error('Login failed', { username: loginData.username, loginType: 'password', code: errors?.code });
			await refetchCodeImgSrc();
			setLoginData((pre) => ({ ...pre, validation: '' }));
			setIsLogining(false);
			throw errors;
		} 
	}
	async function onPasswordLogin(
		loginData: LoginData,
		options: {
			forceToLocale?: boolean,
			switchFrom?: SwitchFromEnum,
			enableRedirect?: boolean
		}
	) {
		try {
			const isError = checkPasswordLoginData(loginData);
			if (!isError) {
				return await onLogin(loginData, options);
			} else {
				throw new Error();
			}
		} catch (e) {
			throw e;
		}
	}

	async function getRefreshPKey ():Promise<string> {
		try {
			const  response = await fetchComposer.post(cloudUrlParser(`${apiDomain.ctcdDomain}/api/AthenaLogin/RefreshPKey`),  {});
			const  data = await response.json();
			return data?.Data?.PWKEY;
		} catch (e) {
			throw e
		}
	}

	async function onGestureLogin(loginData: LoginData) {
		try {
			if (isLogining) return;
			setIsLogining(true);
			initErrorMessage();
			const formatedLanguage = convertApiLocale(loginData.language || currentLocale);
			const gestureKey = await getRefreshPKey();
			// 手勢
			const gestureFirst = MD5(loginData.password);
			const password = MD5(gestureFirst + gestureKey);
            
			const response = await fetchComposer.post(cloudUrlParser(`${apiDomain.ctcdDomain}/api/AthenaLogin/Login`),  {
				TxtUserName: loginData.username,
				Password: password,
				IsGesture: true,
				Language: formatedLanguage,
				SwitchFrom: SwitchFromEnum.Mobile,
			});
			const data = await response.json();
			if (data.Success) {
				cookieService.addUserNames(loginData.username);
				const result = data.Data as SuccessLoginData;	
				if (result.Token) {
					const redisResult = await createUserSession({
						name: result.UserName,
						site: result.SiteName,
						uuid,
					}, {
						token: result.Token
					});
	
					if (!redisResult.success) {
						throw { resError: 'Error: Insert to redis fail' };
					}
					signIn(result.Token, result.UserName, result.SiteName, uuid);
					rumLogger.info('Login success', { username: result.UserName, siteName: result.SiteName, loginType: 'gesture' });
					handleGestureLogin();
					const clientLocaleFromApi = convertClientLocale(result.Language);
					router.replace('/', { locale: clientLocaleFromApi });
				} else if (result.Url) {
					window.location.replace(result.Url)
				}
				return result;
			}

			throw { code: data.Code, resError: `${data.Code}: ${data.Message}` };
		} catch (errors: any) {
			switch (errors.code) {
				case 'SP-1011':
					// wrong account or password
					setErrorMessage(state => ({ ...state, resError: errors.code + ': ' + t('msg_DepositProcessLogin_15') }));
					break;
				case 'SP-1008':
					// locked
					setErrorMessage(state => ({ ...state, resError: errors.code + ': ' + t('msg_DepositProcessLogin_6') }));
					break;
				case 'SP-1009':
					// login too many times
					setErrorMessage(state => ({ ...state, resError: errors.code + ': ' + t('errlogin_toooften') }));
					break;
				default:
					setErrorMessage(state => ({ ...state, resError: 'Login Error', ...errors }));
					break;
			}
			rumLogger.error('Login failed', { username: loginData.username, loginType: 'gesture', code: errors?.code });
			setIsLogining(false);
			throw errors;
		}
	}

	return {
		isLogining,
		loginData,
		errorMessage,
		codeImgSrc,
		setLoginData,
		onPasswordLogin,
		onGestureLogin,
		onChange,
		refetchCodeImgSrc,
		initLoginData,
		initErrorMessage,
		validationToken,
	};
}

const blobToBase64 = (blob: Blob): Promise<string> => {
	return new Promise((resolve) => {
		const reader = new FileReader();
		reader.onloadend = () => resolve(reader.result as string);
		reader.readAsDataURL(blob);
	});
}
